(() => {
  'use strict';

  const TRIGGER_SELECTOR = '[data-size-guide-trigger]';
  const POPUP_SELECTOR = '[data-size-guide-popup]';
  const CLOSE_SELECTOR = '[data-size-guide-close]';
  const OVERLAY_SELECTOR = '[data-size-guide-overlay]';
  const BODY_SELECTOR = '[data-size-guide-body]';

  const GLOBAL_LOCK_ATTR = 'data-theme-scroll-lock-count';
  const GLOBAL_LOCK_CLASS = 'theme-scroll-lock-active';

  const OPEN_MS = 500;
  const CLOSE_MS = 380;
  const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

  const getScrollbarCompensation = () =>
    Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  const acquireScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      const compensation = `${getScrollbarCompensation()}px`;
      root.style.setProperty('--theme-scrollbar-compensation', compensation);
      if (body) body.style.setProperty('--theme-scrollbar-compensation', compensation);
      root.classList.add(GLOBAL_LOCK_CLASS);
      if (body) body.classList.add(GLOBAL_LOCK_CLASS);
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(current + 1));
  };

  const releaseScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);
    const next = Math.max(0, current - 1);

    if (next === 0) {
      root.classList.remove(GLOBAL_LOCK_CLASS);
      if (body) body.classList.remove(GLOBAL_LOCK_CLASS);
      root.removeAttribute(GLOBAL_LOCK_ATTR);
      root.style.removeProperty('--theme-scrollbar-compensation');
      if (body) body.style.removeProperty('--theme-scrollbar-compensation');
      return;
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(next));
  };

  class SizeGuidePopup {
    constructor(popup) {
      this.popup = popup;
      this.panel = popup.querySelector('.sg-panel');
      this.overlay = popup.querySelector('.sg-overlay');
      this.isOpen = false;
      this.token = 0;
      this.anims = [];
      this.safetyTimer = null;
      this.triggerEl = null;

      this._onClose = this._onClose.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onOverlayClick = this._onOverlayClick.bind(this);
    }

    init() {
      this.popup.querySelectorAll(CLOSE_SELECTOR).forEach((btn) => {
        btn.addEventListener('click', this._onClose);
      });

      const overlay = this.popup.querySelector(OVERLAY_SELECTOR);
      if (overlay) overlay.addEventListener('click', this._onOverlayClick);
    }

    _cancelAnims() {
      clearTimeout(this.safetyTimer);
      this.anims.forEach((a) => {
        a.onfinish = null;
        a.cancel();
      });
      this.anims = [];
    }

    // Runs the slide + fade with the Web Animations API (not affected by theme CSS)
    _run(opening, done) {
      this._cancelAnims();

      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = reduce ? 1 : opening ? OPEN_MS : CLOSE_MS;
      const opts = { duration, easing: EASING, fill: 'both' };
      const token = this.token;

      const finish = () => {
        if (token !== this.token) return;
        if (done) done();
      };

      if (!this.panel || typeof this.panel.animate !== 'function') {
        finish();
        return;
      }

      const slide = opening
        ? [{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }]
        : [{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }];
      const fade = opening ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }];

      const panelAnim = this.panel.animate(slide, opts);
      this.anims = [panelAnim];
      if (this.overlay) this.anims.push(this.overlay.animate(fade, opts));

      panelAnim.onfinish = finish;
      this.safetyTimer = setTimeout(finish, duration + 150);
    }

    open(trigger) {
      if (this.isOpen) return;

      this.token += 1;
      this.triggerEl = trigger || null;

      this.popup.hidden = false;
      this.popup.classList.remove('is-closing');
      this.popup.classList.add('is-open');
      this.popup.setAttribute('aria-hidden', 'false');

      this._run(true, null);

      acquireScrollLock();
      this.isOpen = true;

      document.addEventListener('keydown', this._onKeydown);

      const body = this.popup.querySelector(BODY_SELECTOR);
      if (body) body.scrollTop = 0;

      const closeBtn = this.popup.querySelector(CLOSE_SELECTOR);
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;

      this.token += 1;
      this.isOpen = false;
      this.popup.classList.add('is-closing');
      this.popup.setAttribute('aria-hidden', 'true');

      releaseScrollLock();
      document.removeEventListener('keydown', this._onKeydown);

      this._run(false, () => {
        this._cancelAnims();
        this.popup.classList.remove('is-open', 'is-closing');
        this.popup.hidden = true;
      });

      if (this.triggerEl) {
        this.triggerEl.focus({ preventScroll: true });
        this.triggerEl = null;
      }
    }

    _onClose(e) {
      e.preventDefault();
      this.close();
    }

    _onOverlayClick() {
      this.close();
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.close();
      }
    }

    destroy() {
      this.token += 1;
      this._cancelAnims();
      if (this.isOpen) {
        this.isOpen = false;
        releaseScrollLock();
      }
      document.removeEventListener('keydown', this._onKeydown);
      this.popup.classList.remove('is-open', 'is-closing');
      this.popup.setAttribute('aria-hidden', 'true');
      this.popup.hidden = true;
    }
  }

  const instances = new Map();

  const copySchemeClasses = (from, to) => {
    if (!from) return;
    from.classList.forEach((c) => {
      if (c.indexOf('color-scheme') === 0) to.classList.add(c);
    });
  };

  // Detach the popup from the theme's shared modal system (its CSS/JS fights the slide)
  const detachFromThemeModal = (popup) => {
    popup.classList.remove('theme-modal--perspective');
    const overlay = popup.querySelector('.theme-modal__overlay');
    if (overlay) {
      overlay.classList.remove('theme-modal__overlay');
      overlay.classList.add('sg-overlay');
    }
    const panel = popup.querySelector('.theme-modal__panel');
    if (panel) {
      panel.classList.remove('theme-modal__panel');
      panel.classList.add('sg-panel');
    }
  };

  // Finds the option group whose label reads "Size"
  const findSizeGroup = (picker) => {
    const walker = document.createTreeWalker(picker, NodeFilter.SHOW_TEXT);
    let node;
    let label = null;
    while ((node = walker.nextNode())) {
      if (/^\s*size\s*:?\s*$/i.test(node.nodeValue)) {
        label = node.parentElement;
        break;
      }
    }
    if (!label) return null;
    let el = label;
    while (el && el !== picker) {
      if (el.querySelector('input, select, button')) return el;
      el = el.parentElement;
    }
    return picker;
  };

  // Moves the "Size guide" button onto the Size picker row
  const mountTrigger = (trigger) => {
    const block = trigger.closest('.product-block');
    const form = trigger.closest('form') || document;
    const picker = form.querySelector('.product-block--variant_picker');
    if (!picker) return;

    const place = () => {
      if (picker.contains(trigger)) return;
      const group = findSizeGroup(picker);
      if (!group) return;
      group.classList.add('size-guide-host');
      trigger.classList.add('size-guide-trigger--inline');
      group.appendChild(trigger);
      if (block) block.style.display = 'none';
    };

    place();
    if ('MutationObserver' in window) {
      new MutationObserver(place).observe(picker, { childList: true, subtree: true });
    }
  };

  const initSizeGuide = (container) => {
    const popup = container.querySelector(POPUP_SELECTOR);
    if (!popup || instances.has(popup)) return;

    const triggers = Array.from(container.querySelectorAll(TRIGGER_SELECTOR));

    detachFromThemeModal(popup);
    copySchemeClasses(container.closest('.color-scheme'), popup);
    document.body.appendChild(popup);

    const controller = new SizeGuidePopup(popup);
    controller.init();
    instances.set(popup, { controller, container });

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        controller.open(trigger);
      });
      mountTrigger(trigger);
    });
  };

  document.querySelectorAll('[data-size-guide-section]').forEach(initSizeGuide);

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target) {
      e.target.querySelectorAll('[data-size-guide-section]').forEach(initSizeGuide);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    if (!e.target) return;
    instances.forEach((entry, popup) => {
      if (e.target.contains(entry.container)) {
        entry.controller.destroy();
        popup.remove();
        instances.delete(popup);
      }
    });
  });
})();